import { DatePipe } from '@angular/common';
import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { FormBuilder, FormControl, FormGroup, Validators, AbstractControl, ValidatorFn } from '@angular/forms';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { MessagingServiceService } from 'src/app/Services/messaging-service.service';
import { ProjectService } from 'src/app/Services/project.service';
import { SharedService } from 'src/app/Services/sharedService';
import { ApplyCouponComponent } from 'src/app/components/apply-coupon/apply-coupon.component';
import swal from 'sweetalert2';
import { passwordValidator } from 'src/app/password-validators';
import { superseeConfig } from 'src/app/superseeConfig';
import { normalizeTeamSizeForSelect } from 'src/app/onboarding-checklist/team-size.util';
import { WoocommerceService } from 'src/app/Services/woocommerce.service';
import { Country, State } from 'src/app/models/woocommerce.model';
import { Observable, of, forkJoin } from 'rxjs';
import { startWith, map, tap, switchMap, catchError } from 'rxjs/operators';
import { AuthService } from 'src/app/Services/auth.service';
import { PhoneVerificationService } from 'src/app/Services/phone-verification.service';
import { PhoneVerificationModalComponent } from 'src/app/components/phone-verification-modal/phone-verification-modal.component';
import { TokenEncryptionService } from 'src/app/Services/token-encryption.service';

@Component({
    selector: 'app-user-profile-info',
    standalone: true,
    imports: [
      CommonModule,
      FormsModule,
      ReactiveFormsModule,
      MatSlideToggleModule,
      MatFormFieldModule,
      MatInputModule,
      MatSelectModule,
      MatAutocompleteModule,
      MatIconModule,
      MatCardModule,
      MatTooltipModule,
      NgxSkeletonLoaderModule,
    ],
    templateUrl: './user-profile-info.component.html',
    styleUrls: ['./user-profile-info.component.css'],
    providers: [DatePipe],
})
export class UserProfileInfoComponent {
  hideCurrent: boolean = true;
  hideNew: boolean = true;
  hideConfirm: boolean = true;
  changePasswordForm: FormGroup;
  userProfileForm: FormGroup;
  userSettingForm: FormGroup;
  billingAddressForm: FormGroup;
  id;
  role = '';
  userName = '';
  quotaStatus = '';
  quota_expired_date;
  totalUser = 0;
  availableUser = 0;
  useUser = 0;
  isAdmin = true;
  currentQuotaStatus = '';
  loading = false;
  company_name = '';
  plan_type = '';
  userProfileEditable = false;
  teamSizeOptions = superseeConfig.teamSizeOptions;
  countries: Country[] = [];
  filteredCountries: Country[] = [];
  countryControl = new FormControl('');
  states: State[] = [];
  filteredStates: State[] = [];
  stateControl = new FormControl('');
  initialStateCode: string | null = null;
  hasStates: boolean = false; // Track if states are available for the selected country

  // New properties for company name display logic
  showCompanyNameInput: boolean = false;
  currentCompanyName: string = '';
  
  // Email verification status
  emailVerified: boolean = false;
  isSendingEmailVerification: boolean = false;


  private _updateUserPropertiesAndForm(data: any): void {
    this.plan_type = data.meta.plan_name ? data.meta.plan_name : '';
    this.company_name = data.meta.company_name ? data.meta.company_name : '';
    this.tokenEncryptionService.setEncryptedItem('user', data);
    let isExpired = this._sharedService.checkPlanExpired(data);
    if (isExpired) {
      this.router.navigateByUrl('/plan-expired');
      return; // Exit early if plan is expired
    }
    let roleArr = data.role;
    this.id = data.id;
    const derived_user_role = roleArr.length ? (roleArr[0] === 'employee' ? roleArr[1] : roleArr[0]) : '';
    this.role = derived_user_role;

    const isNameEditable = !data.name || data.name === 'undefined' || data.name === null || data.name.trim().toLowerCase() === 'undefined undefined';
    const companyNameFromMeta = data.meta.company_name;
    const isCompanyNameEffectivelyMissing = !companyNameFromMeta || companyNameFromMeta === 'undefined' || companyNameFromMeta === null || companyNameFromMeta.trim() === '';

    const normalizedTeamSize = normalizeTeamSizeForSelect(data.meta.teamSize);
    const isTeamSizeEditable = !normalizedTeamSize;

    let adminRoleCheck = roleArr.filter((ele) => (ele.toLowerCase().includes("admin") || ele.toLowerCase().includes("company")));
    this.isAdmin = adminRoleCheck.length > 0;

    this.currentCompanyName = isCompanyNameEffectivelyMissing ? '' : companyNameFromMeta;
    this.showCompanyNameInput = this.isAdmin && isCompanyNameEffectivelyMissing;

    this.userProfileEditable = this.isAdmin && (isNameEditable || isCompanyNameEffectivelyMissing || isTeamSizeEditable);


    this.userProfileForm = this.formBuilder.group({
      name: new FormControl({ value: (!data.name || data.name.trim().toLowerCase() === 'undefined undefined') ? '' : data.name, disabled: !isNameEditable }, Validators.required),
      email: new FormControl({ value: data.email, disabled: true }),
      role: new FormControl({ value: this.role === 'company' ? 'admin' : this.role, disabled: true }),
      company_name: new FormControl(
        {
          value: this.currentCompanyName,
          disabled: !this.showCompanyNameInput
        },
        Validators.required
      ),
      team_size: new FormControl({ value: normalizedTeamSize, disabled: !this.isAdmin || !isTeamSizeEditable }, Validators.required)
    });
  }

  private _updateQuotaDetails(data: any): void {
    this.quotaStatus = '';
    let quota_Status = data.meta.quota_status;
    this.currentQuotaStatus = quota_Status;
    if (['active', 'Active', 'trial', 'Trial'].includes(quota_Status)) {
      this.quotaStatus = 'active';
      if (data.meta.expires_at) {
        if (this.getRemainingDate(data.meta.expires_at) < 0) this.quotaStatus = 'expired';
      }
    }
    if (['trial-expired', 'Trial-expired', 'expired', 'Expired'].includes(quota_Status)) {
      this.quotaStatus = 'expired';
    }
    if (data.meta.expires_at) this.quota_expired_date = this.formattedDate(data.meta.expires_at);
    this.totalUser = data.meta.quota_allowed < 0 ? 0 : data.meta.quota_allowed;
    this.availableUser = (data.meta.quota_remaining === 'false' || data.meta.quota_remaining === false) ? 0 : data.meta.quota_remaining;
    this.useUser = (data.meta.quota_allowed - this.availableUser) < 0 ? 0 : (data.meta.quota_allowed - this.availableUser);
    this.availableUser = this.availableUser < 0 ? 0 : this.availableUser;
  }

  constructor(public _dialog: MatDialog, public formBuilder: FormBuilder, private _projectService: ProjectService, private _snackBar: MatSnackBar, private datePipe: DatePipe, private _sharedService: SharedService, private _messagingService: MessagingServiceService, private router: Router, private woocommerceService: WoocommerceService, private authService: AuthService, private phoneVerificationService: PhoneVerificationService, private tokenEncryptionService: TokenEncryptionService) {
    this.loading = true;

    // Country autocomplete filtering
    this.countryControl.valueChanges.pipe(
      startWith(''),
      map(value => this._filterCountries(value || ''))
    ).subscribe(filtered => this.filteredCountries = filtered);

    // State autocomplete filtering
    this.stateControl.valueChanges.pipe(
      startWith(''),
      map(value => this._filterStates(value || ''))
    ).subscribe(filtered => this.filteredStates = filtered);
  }

  ngOnInit(): void {
    this.loading = true;
    this.createProfileForm();
    this.createSettingsForm();
    this.createChangePasswordForm();
    this.createBillingAddressForm();

    // Start the chained data loading process
    this.fetchCountriesObservable().pipe(
      switchMap(() => this.getUserAndRelatedDataObservable())
    ).subscribe({
      next: () => {
        this.loading = false;
      },
      error: (err) => {
        console.error("Error initializing component data:", err);
        this.loading = false;
      }
    });
  }

  ngAfterViewInit(): void {
  }

  createProfileForm() {
    this.userProfileForm = this.formBuilder.group({
      name: new FormControl(''),
      email: new FormControl(''),
      role: new FormControl(''),
      company_name: new FormControl(''),
      team_size: new FormControl('')
    })
  }

  createSettingsForm() {
    let loginUser = this.tokenEncryptionService.getEncryptedItem<any>('user');
    this.userSettingForm = this.formBuilder.group({
      disable_toast: new FormControl(loginUser?.meta?.disable_toast || false),
    })
  }

  onDisableToast(event) {
    let loginUser = this.tokenEncryptionService.getEncryptedItem<any>('user');

    loginUser.meta.disable_toast = event
      this._projectService.updateOwnSettings(loginUser.id, { meta: { disable_toast: event } }).subscribe((response) => {
      this.tokenEncryptionService.setEncryptedItem('user', loginUser);
    }, (error) => {
      console.log(error)
    })
  }

  createChangePasswordForm() {
    this.changePasswordForm = this.formBuilder.group({
      currentPassword: new FormControl('', Validators.required),
      newPassword: new FormControl("", [Validators.required, Validators.minLength(8), passwordValidator()]),
      confirmPassword: new FormControl('', Validators.required),
    }, { validators: this.passwordMatchValidator });
  }

  passwordMatchValidator(form: any) {
    return form.get('newPassword').value === form.get('confirmPassword').value ? null : { 'mismatch': true };
  }

  createBillingAddressForm() {
    this.billingAddressForm = this.formBuilder.group({
      country: new FormControl('', Validators.required),
      state: new FormControl(''),
      addressLine1: new FormControl(''),
      addressLine2: new FormControl(''),
      city: new FormControl(''),
      zipCode: new FormControl(''),
    });
  }

  fetchCountriesObservable(): Observable<Country[]> {
    return this.woocommerceService.getCountries().pipe(
      tap((response: Country[]) => {
        this.countries = response;
        this.filteredCountries = response;
      }),
      catchError(err => {
        this.countries = [];
        this.filteredCountries = [];
        console.error('Error fetching countries:', err);
        this._snackBar.open('Failed to load countries list.', 'Close', { duration: 3000 });
        return of([]);
      })
    );
  }

  getUserAndRelatedDataObservable(): Observable<any> {
    return this._projectService.getUser().pipe(
      tap((data: any) => {
        console.log("data himashu is checking", data);
        if(data.meta.phone_number && data.meta.phone_number !== 'undefined' && data.meta.phone_number !== null && data.meta.phone_number !== 'none' && data.meta.phone_number.trim().toLowerCase() !== 'null-undefined'){
          this.phoneVerificationService.setVerificationStatus(data.meta.phone_number);
        } else {
          this.phoneVerificationService.setVerificationStatus(null);
        }
        
        // Check email verification status
        this.emailVerified = data.verified === '1' || data.verified === 1 || data.verified === true || data.verified === 'true';
        
        this._updateUserPropertiesAndForm(data);
        if (this.router.url === '/plan-expired') {
          return;
        }
        this._updateQuotaDetails(data);
      }),
      switchMap((data: any) => {
        if (this.router.url === '/plan-expired') {
          return of(null);
        }
        if (this.id && !this._sharedService.checkPlanExpired(data)) {
          const isPhoneEditable = !data.meta.phone_number || data.meta.phone_number === 'undefined' || data.meta.phone_number === null || data.meta.phone_number === 'none' || data.meta.phone_number.trim().toLowerCase() === 'null-undefined';
          return this.loadAndPrefillBillingAddressObservable(this.id, data.meta.phone_number, isPhoneEditable);
        }
        return of(null);
      })
    );
  }

  loadAndPrefillBillingAddressObservable(customerId: number, userMetaPhone: string, isPhoneEditable: boolean): Observable<any> {
    return this.woocommerceService.getCustomerDetails(customerId).pipe(
      switchMap(customer => {
        const billing = customer.billing;
        const phoneToPrefill = (billing.phone && billing.phone !== 'none') ? billing.phone : ((!userMetaPhone || userMetaPhone.trim().toLowerCase() === 'null-undefined') ? '' : userMetaPhone);

        this.billingAddressForm.patchValue({
          addressLine1: billing.address_1,
          addressLine2: billing.address_2,
          city: billing.city,
          zipCode: billing.postcode,
        });

        const countryCode = billing.country;
        this.initialStateCode = billing.state; // Used by onCountrySelected

        if (countryCode) {
          const country = this.countries.find(c => c.code === countryCode);
          if (country) {
            // Set the value for the autocomplete input display
            this.countryControl.setValue(country.name, { emitEvent: false }); // emitEvent: false as we call onCountrySelected manually

            // Manually trigger the logic that selecting a country would perform
            this.onCountrySelected(country.name);
          } else {
            // Country code from billing data not found in our list of countries
            this.countryControl.setValue('');
            this.onCountrySelected(null);
          }
        } else {
          // No country code in billing data
          this.countryControl.setValue('');
          this.onCountrySelected(null);
        }
        return of(customer);
      }),
      catchError(err => {
        console.error('Error fetching or prefilling billing details:', err);
        this._snackBar.open('Failed to load billing details.', 'Close', { duration: 3000 });

        // Ensure form is reset in case of error
        this.countryControl.setValue('');
        // Check if onCountrySelected is defined before calling, just in case of an extreme initialization error
        if (typeof this.onCountrySelected === 'function') {
          this.onCountrySelected(null);
        } else {
          // Fallback manual reset if onCountrySelected is somehow not available
          this.stateControl.setValue('');
          this.billingAddressForm.get('state').setValue('');
          this.states = [];
          this.filteredStates = [];
          this.hasStates = false;
          this.updateStateValidation(null); // Explicitly call if onCountrySelected cannot
          this.initialStateCode = null;
        }
        return of(null);
      })
    );
  }

  changePasswordAction() {
    if (!this.changePasswordForm.valid || this.changePasswordForm.get('newPassword').value !=
      this.changePasswordForm.get('confirmPassword').value) {
      return
    } else {
      this
        ._projectService
        .changePasword(this.changePasswordForm.value.currentPassword, this.changePasswordForm.value.newPassword, this.id).subscribe((user: any) => {
          swal.fire({
            icon: 'success',
            title: 'Success',
            text: 'Password change successfully',
            timer: 2000,
            showConfirmButton: false,
          }).then(() => {
            this.authService.performLogout();
          });
        }, err => {
          this._snackBar.open(err.error.message, 'Undo', {
            duration: 3000
          })
        })
    }
  }

  isTourActive() {
    if (localStorage.getItem('isTour')) {
      if (localStorage.getItem('isTour') == 'false') {
        return true;
      } else {
        return false;
      }
    } else {
      return true;
    }
  }

  formattedDate(expiresAt): string {
    const parsedDate = new Date(expiresAt);
    const formattedDate = this.datePipe.transform(parsedDate, 'MMM dd, yyyy');

    return formattedDate || '';
  }
  getRemainingDate(strDate) {
    const expirationDate = new Date(strDate);
    const timeDifference = expirationDate.getTime() - new Date().getTime();
    const daysDifference = timeDifference / (1000 * 60 * 60 * 24);
    let daysRemaining = Math.round(daysDifference);
    return daysRemaining;
  }

  applyCouponAction() {
    const dialogConfig = new MatDialogConfig();

    dialogConfig.autoFocus = true;

    dialogConfig.minWidth = 400;

    const dialogRef = this._dialog.open(ApplyCouponComponent, dialogConfig);

    dialogRef.afterClosed().subscribe(result => {
      console.log(result, "result")
      if (result && result.couponCode) {
        this._snackBar.open('Coupon applied successfully', 'Undo', {
          duration: 3000
        })
        this.getUserAndRelatedDataObservable().subscribe();
      }

    });
  }

  saveUserProfile() {
    if (this.userProfileForm.invalid) {
      this._snackBar.open('Please fill all required fields.', 'Close', { duration: 3000 });
      return;
    }
    let userData = {
      name: this.userProfileForm.value.name,
      meta: {
        company_name: this.userProfileForm.value.company_name,
        teamSize: this.userProfileForm.value.team_size
      }
    };
    this._projectService.updateOwnSettings(this.id, userData).subscribe(
      (response) => {
        this._snackBar.open('Profile updated successfully!', 'Close', { duration: 3000 });
        this.getUserAndRelatedDataObservable().subscribe();
      },
      (error) => {
        this._snackBar.open('Failed to update profile.', 'Close', { duration: 3000 });
      }
    );
  }


  saveBillingAddress() {
    this.billingAddressForm.markAllAsTouched(); // Ensure all fields show validation state on save attempt

    if (this.billingAddressForm.invalid) {
      this._snackBar.open('Please fill all required billing address fields', 'Close', {
        duration: 3000,
      });
      return;
    }

    // Get verified phone number
    const verifiedPhone = this.phoneVerificationService.getVerificationStatus().phoneNumber;
    const selectedCountry = this.countries.find(c => c.name === this.countryControl.value);
    const selectedState = this.states.find(s => s.name === this.stateControl.value);

    const userName = this.userProfileForm.value.name || ''; // Default to empty string if name is undefined

    const billingData = {
      first_name: userName.split(' ')[0] || '',
      last_name: userName.split(' ').slice(1).join(' ') || '',
      company: this.userProfileForm.value.company_name || '',
      address_1: this.billingAddressForm.value.addressLine1,
      address_2: this.billingAddressForm.value.addressLine2,
      city: this.billingAddressForm.value.city,
      state: selectedState ? selectedState.code : '',
      postcode: this.billingAddressForm.value.zipCode,
      country: selectedCountry ? selectedCountry.code : '',
      email: this.userProfileForm.value.email,
      phone: verifiedPhone || '',
    };

    this.woocommerceService.updateCustomerBilling(this.id, { billing: billingData }).subscribe(response => {
      this._snackBar.open('Billing address updated successfully', 'Close', {
        duration: 3000,
      });
    }, error => {
      console.error('Error updating billing address:', error);
      this._snackBar.open('Failed to update billing address', 'Close', {
        duration: 3000,
      });
    });
  }

  private _filterCountries(value: string): Country[] {
    const filterValue = value.toLowerCase();
    return this.countries.filter(country => country.name.toLowerCase().includes(filterValue));
  }

  private _filterStates(value: string): State[] {
    const filterValue = value.toLowerCase();
    return this.states.filter(state => state.name.toLowerCase().includes(filterValue));
  }

  onCountrySelected(countryName: string | null) {
    const selectedCountry = countryName ? this.countries.find(c => c.name === countryName) : null;

    // Update the visual input control (countryControl) to keep it in sync
    this.countryControl.setValue(selectedCountry ? selectedCountry.name : '', { emitEvent: false });

    // Reset state fields
    this.stateControl.setValue('');
    this.billingAddressForm.get('state').setValue('');
    this.states = [];
    this.filteredStates = [];
    this.hasStates = false;

    const countryFormControl = this.billingAddressForm.get('country');
    if (!countryFormControl) return; // Guard clause

    if (selectedCountry) {
      countryFormControl.setValue(selectedCountry.name);

      const processAndSetState = (statesToProcess?: State[]) => {
        if (statesToProcess && statesToProcess.length > 0) {
          this.states = statesToProcess;
          this.filteredStates = statesToProcess;
          this.hasStates = true;

          // If an initial state code was stored (during prefill)
          if (this.initialStateCode) {
            const initialCodeLower = this.initialStateCode.toLowerCase();
            const stateToSet = this.states.find(s => s.code.toLowerCase() === initialCodeLower);
            if (stateToSet) {
              this.stateControl.setValue(stateToSet.name); // Set autocomplete display
              this.billingAddressForm.get('state').setValue(stateToSet.name); // Set form value for state
            }
          }
        } else {
          // No states available
          this.states = [];
          this.filteredStates = [];
          this.hasStates = false;
          // Clear state values when no states are available
          this.stateControl.setValue('');
          this.billingAddressForm.get('state').setValue('');
        }
        // Clear initialStateCode after attempting to use it, regardless of success with this country's states
        this.initialStateCode = null;
        this.updateStateValidation(selectedCountry.name);
      };

      if (selectedCountry.states && selectedCountry.states.length > 0) {
        // States are already available with the country object
        processAndSetState(selectedCountry.states);
      } else {
        // States are not available, try to fetch them for the selected country
        // This assumes you will add a method like getStatesForCountry in your WoocommerceService
        this.woocommerceService.getStatesForCountry(selectedCountry.code).subscribe(
          (fetchedStates: State[]) => {
            // Optionally, update the country object in this.countries to cache these states
            const countryInList = this.countries.find(c => c.code === selectedCountry.code);
            if (countryInList) {
              countryInList.states = fetchedStates;
            }
            processAndSetState(fetchedStates);
          },
          (error) => {
            console.error(`Error fetching states for ${selectedCountry.name}:`, error);
            // Don't show error message - just hide the state field
            processAndSetState([]); // Process with empty states, which will clear initialStateCode
          }
        );
      }
    } else {
      // No country selected (e.g., field cleared or countryName was null)
      this.billingAddressForm.get('country').setValue('');
      this.hasStates = false;
      this.updateStateValidation(null);
      this.initialStateCode = null; // Important to clear if no country
    }

    // Mark the control as touched and dirty to trigger validation messages
    countryFormControl.markAsTouched();
    countryFormControl.markAsDirty();
  }

  updateStateValidation(countryName: string | null) {
    const stateControl = this.billingAddressForm.get('state');
    const countryControl = this.billingAddressForm.get('country');

    if (stateControl && countryControl) { // Ensure controls exist
      // Make state required only if states are available for the selected country
      if (this.hasStates && this.states.length > 0) {
        stateControl.setValidators(Validators.required);
      } else {
        stateControl.clearValidators();
      }
      stateControl.updateValueAndValidity();
    }
  }

  onStateSelected(stateName: string) {
    const selectedState = this.states.find(s => s.name === stateName);
    if (selectedState) {
      // Using name for both display and model value as per current setup for country.
      // If you need to save state code, use selectedState.code.
      this.billingAddressForm.get('state').setValue(selectedState.name);
    } else {
      // Fallback or if user types a state not in the list (if free-form entry is allowed)
      this.billingAddressForm.get('state').setValue(stateName);
    }
  }

  onCountryInputBlur(): void {
    const countryFormCtrl = this.billingAddressForm.get('country');
    if (!countryFormCtrl) {
      return;
    }

    const currentInputText = this.countryControl.value;
    // Ensure to handle null or undefined currentInputText before calling toLowerCase
    const matchedCountry = this.countries.find(c => c.name.toLowerCase() === (currentInputText || '').toLowerCase());

    if (matchedCountry) {
      // Input text is a valid country name.
      // Ensure it's properly selected if not already, or if case is different.
      if (countryFormCtrl.value !== matchedCountry.name || this.countryControl.value !== matchedCountry.name) {
        this.onCountrySelected(matchedCountry.name); // This will sync both controls and handle states
      }
    } else {
      // Input text is not a valid country name (or empty).
      // Clear the selection. onCountrySelected(null) handles clearing both controls and dependent state.
      // Call onCountrySelected(null) only if something needs to be cleared to avoid unnecessary event cycles.
      if (countryFormCtrl.value !== '' || (currentInputText && currentInputText.trim() !== '')) {
        this.onCountrySelected(null);
      }
    }
    countryFormCtrl.markAsTouched();
  }

  onSelectCountry(event) {
    this.initialStateCode = null; // Reset initial state when country changes
  }

  /**
   * Check if email is verified
   */
  isEmailVerified(): boolean {
    return this.emailVerified;
  }

  /**
   * Send email verification
   */
  sendEmailVerification(): void {
    const email = this.userProfileForm.get('email')?.value;
    if (!email) {
      this._snackBar.open('No email address found.', 'Close', { duration: 3000 });
      return;
    }

    this.isSendingEmailVerification = true;
    this._projectService.sendVarificationCode(email).subscribe(
      (response: any) => {
        this.isSendingEmailVerification = false;
        // Keep current_email for legacy flows (verification-code uses encrypted value)
        localStorage.setItem('current_email', email);
        this._snackBar.open('Verification email sent! Please check your inbox.', 'Close', { duration: 5000 });
        this.router.navigate(['/verification-code'], { queryParams: { type: 'email-verify' } });
      },
      (error) => {
        this.isSendingEmailVerification = false;
        this._snackBar.open(error.error?.message || 'Failed to send verification email.', 'Close', { duration: 3000 });
      }
    );
  }

  /**
   * Check if phone is verified
   */
  isPhoneVerified(): boolean {
    return this.phoneVerificationService.isPhoneVerified();
  }

  /**
   * Get verified phone number for display
   */
  getVerifiedPhoneNumber(): string {
    const status = this.phoneVerificationService.getVerificationStatus();
    return status.phoneNumber || 'No phone number verified';
  }

  /**
   * Open phone verification modal
   */
  openPhoneVerificationModal(): void {
    const dialogRef = this._dialog.open(PhoneVerificationModalComponent, {
      width: window.innerWidth < 600 ? '100vw' : '90vw',
      height: 'auto',
      maxWidth: window.innerWidth < 600 ? '100vw' : '500px',
      maxHeight: window.innerWidth < 600 ? '85vh' : '95vh',
      disableClose: true,
      panelClass: ['phone-verification-dialog', window.innerWidth < 600 ? 'mobile-dialog' : ''],
      data: {
        title: 'Verify Your Phone Number',
        message: 'Please verify your phone number to add billing information.',
        skipable: false
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.verified) {
        this._snackBar.open('Phone number verified successfully! You can now add billing information.', 'Close', {
          duration: 3000,
        });
      }
    });
  }

}
